import React, { useState } from "react";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { FilePdf } from "@phosphor-icons/react";
import { resolveLogoUrl } from "@/components/LogoUploader";

// Carga una imagen como dataURL (para encabezado del PDF); null si falla
function toDataUrl(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve({ data: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function buildDocument({ companyName, logo, sections }) {
  const root = document.createElement("div");
  root.style.cssText = "width:190mm;background:#fff;color:#171717;font-family:inherit";
  const date = new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
  root.innerHTML = `
    <div style="height:257mm;display:flex;flex-direction:column;justify-content:center;align-items:flex-start;padding:0 12mm;border-left:6px solid #c8102e">
      ${logo ? `<img src="${logo.data}" style="max-height:90px;max-width:220px;object-fit:contain;margin-bottom:28px"/>` : ""}
      <div style="font-size:12px;letter-spacing:.2em;color:#c8102e;font-weight:700">ZAE DOCUMENTAL</div>
      <div style="font-size:40px;font-weight:900;line-height:1.05;margin-top:8px">Manual de usuario</div>
      <div style="font-size:18px;color:#404040;margin-top:14px">${companyName}</div>
      <div style="font-size:12px;color:#737373;margin-top:40px">Generado el ${date}</div>
    </div>
    <div style="page-break-before:always;padding-top:4mm">
      <div style="font-size:24px;font-weight:900;margin-bottom:12px">Contenido</div>
      <ol style="font-size:13px;line-height:2;padding-left:20px;color:#262626">${sections.map(s => `<li>${s.label}</li>`).join("")}</ol>
    </div>`;
  const content = document.getElementById("manual-content").cloneNode(true);
  content.querySelectorAll(".manual-section").forEach(s => {
    s.style.pageBreakBefore = "always"; s.style.border = "none"; s.style.padding = "0";
  });
  content.querySelectorAll("img[loading]").forEach(i => i.removeAttribute("loading"));
  root.appendChild(content);
  return root;
}

export default function ManualPdfButton({ sections }) {
  const { companies, activeCompanyId, user } = useAuth();
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    const t = toast.loading("Generando PDF del manual…");
    try {
      const { default: html2pdf } = await import("html2pdf.js");
      const company = (companies || []).find(c => c.id === (activeCompanyId || user?.active_company_id || user?.company_id));
      const logo = (company?.logo_url && await toDataUrl(resolveLogoUrl(company.logo_url))) || await toDataUrl("/ZAE_ROJO.png");
      const companyName = company?.name || "ZAE Documental";
      const el = buildDocument({ companyName, logo, sections });
      const opt = {
        margin: [18, 10, 14, 10],
        filename: `Manual_ZAE_Documental_${companyName.replace(/[^\w-]+/g, "_")}.pdf`,
        image: { type: "jpeg", quality: 0.9 },
        html2canvas: { scale: 1.6, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"], avoid: ["li", "figure", "h4", "tr"] },
      };
      await html2pdf().set(opt).from(el).toPdf().get("pdf").then(pdf => {
        const n = pdf.internal.getNumberOfPages();
        const lh = 8, lw = logo ? Math.min(30, (logo.w / logo.h) * lh) : 0;
        for (let i = 2; i <= n; i++) {
          pdf.setPage(i);
          if (logo) pdf.addImage(logo.data, "PNG", 10, 5, lw, lh);
          pdf.setFontSize(8); pdf.setTextColor(115);
          pdf.text(`Manual de usuario · ${companyName}`, 200, 10, { align: "right" });
          pdf.setDrawColor(229); pdf.line(10, 14.5, 200, 14.5);
          pdf.text(`Página ${i} de ${n}`, 200, 290, { align: "right" });
        }
      }).save();
      toast.success("PDF descargado", { id: t });
    } catch (e) {
      toast.error("No se pudo generar el PDF", { id: t });
    } finally { setBusy(false); }
  };

  return (
    <button onClick={generate} disabled={busy} data-testid="manual-download-pdf-btn"
      className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-semibold disabled:opacity-60 transition-colors">
      <FilePdf size={18} weight="duotone"/> {busy ? "Generando…" : "Descargar PDF"}
    </button>
  );
}
